import React, { FC, useEffect, useMemo } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box } from '@altermaind/web/ui';
import { PaginatedCards, usePaginatedTable } from '@/_shared/components';
import { useNotification } from '@/_shared/hooks/useNotification';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { ContentType } from '@/swagger/constants/contents-orchestrator-enums';
import { getContentsByTypeCall, getMyContentsCall } from '../../state';
import { Cards } from '../Cards';
import { ContentToCard } from '../ContentToCard';
import { EmptyState } from '../EmptyState';
import { FilterContents } from '../FilterContents/FilterContents';

type Props = {
  filterType?: `${ContentType}`;
  myContents?: boolean;
  hasEmptyStateAlert?: boolean;
};

export const PaginatedContents: FC<Props> = ({
  filterType,
  myContents = false,
  hasEmptyStateAlert = false,
}) => {
  const t = useTranslations('trainingSoftSkills.sections.paginatedContents.title');
  const tShared = useTranslations();
  const { notify } = useNotification();
  const { genericResponsiveValue } = useResponsiveStyle();
  const {
    execute: executeGetContentsByType,
    loading: loadingGetContentsByType,
    data,
    response: responseGetContentsByType,
    error: errorGetContentsByType,
    fullRequest: fullRequestGetContentsByType,
  } = getContentsByTypeCall.hook();

  const {
    execute: executeGetMyContents,
    loading: loadingGetMyContents,
    data: myContentsData,
    response: responseGetMyContents,
    error: errorGetMyContents,
    fullRequest: fullRequestGetMyContents,
  } = getMyContentsCall.hook();

  const executeToUse = useMemo(
    () => (filterType ? executeGetContentsByType : executeGetMyContents),
    [executeGetContentsByType, executeGetMyContents, filterType],
  );

  const PAGE_SIZE = genericResponsiveValue<number>({
    breakPoints: {
      mobile: 20,
      tablet: 20,
      desktopLarge: 20,
    },
    defaultValue: 21,
  });

  const numberOfCardsInSingleRow = genericResponsiveValue<number>({
    breakPoints: {
      mobile: 1,
      tablet: 2,
      desktopLarge: 4,
    },
    defaultValue: 3,
  });

  const callToUse = useMemo(
    () => (filterType ? getContentsByTypeCall : getMyContentsCall),
    [filterType],
  );

  const loadingToUse = useMemo(
    () => (filterType ? loadingGetContentsByType : loadingGetMyContents),
    [filterType, loadingGetContentsByType, loadingGetMyContents],
  );
  const dataToUse = useMemo(
    () => (filterType ? data : myContentsData),
    [data, filterType, myContentsData],
  );
  const responseToUse = useMemo(
    () => (filterType ? responseGetContentsByType : responseGetMyContents),
    [filterType, responseGetContentsByType, responseGetMyContents],
  );
  const errorToUse = useMemo(
    () => (filterType ? errorGetContentsByType : errorGetMyContents),
    [filterType, errorGetContentsByType, errorGetMyContents],
  );

  const fullRequestToUse = useMemo(
    () => (filterType ? fullRequestGetContentsByType : fullRequestGetMyContents),
    [filterType, fullRequestGetContentsByType, fullRequestGetMyContents],
  );

  const paginedProps = usePaginatedTable({
    rowData: dataToUse || [],
    pageSize: PAGE_SIZE,
    totalItems: responseToUse?.totalItems || 0,
  });

  const hasActiveFilters =
    (fullRequestToUse?.payload?.filters && fullRequestToUse.payload.filters.length > 0) ||
    !!fullRequestToUse?.payload?.search ||
    !!fullRequestToUse?.payload?.tag;

  useEffect(() => {
    executeToUse({
      type: (filterType || undefined) as never,
      length: PAGE_SIZE,
      errorSaga: () => {
        notify(tShared('errors.genericApiError'), 'error');
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executeGetContentsByType, executeGetMyContents, executeToUse, filterType, tShared]);

  if (errorToUse && hasEmptyStateAlert) {
    return <EmptyState />;
  }

  if (
    hasEmptyStateAlert &&
    !loadingToUse &&
    (!dataToUse || dataToUse.length === 0) &&
    !hasActiveFilters
  ) {
    return <EmptyState />;
  }

  return (
    <Box>
      <FilterContents
        title={filterType ? t(`${filterType}`) : t('saved')}
        titleProps={{
          variant: 'h2',
        }}
        call={callToUse}
        showFiltersComponent={!filterType}
        hideSearch={myContents}
        hideTags={myContents}
        hideOrderFilter={myContents}
      />
      <PaginatedCards
        pageSize={PAGE_SIZE}
        elementsForRow={numberOfCardsInSingleRow}
        loading={loadingToUse}
        paginedProps={paginedProps}
        call={filterType ? { ...getContentsByTypeCall } : { ...getMyContentsCall }}
        renderCard={item => <ContentToCard content={item} key={item.id} />}
        cardGap='s'
        skeleton={<Cards.Document loading />}
      />
    </Box>
  );
};
